import {
  COLLAB_COMMENT_MAX,
  EVIDENCE_ACCEPT,
  collabCommentInputSchema,
  collabItemSchema,
  collabLinkInputSchema,
  collabListSchema,
  evidenceFileProblem,
  formatDateTime,
  type CollabItem,
  type CollabSubjectName,
} from '@fluxus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Camera, Download, FileText, Link2, Paperclip, ShieldCheck, Trash2 } from 'lucide-react';
import { useId, useRef, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Dialog } from '@/components/ui/dialog';
import { FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { errorMessage } from '@/lib/api/errors';
import { useAuth } from '@/lib/auth/session';
import { apiFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';
import { MentionTextarea } from '@/features/notifications/MentionTextarea';
import { mentionsInText, splitMentions, type MentionRef } from '@/features/notifications/mentions';

const textareaClass =
  'w-full resize-y rounded-md border border-border-strong bg-surface-raised px-3 py-2 text-sm shadow-sm focus-visible:border-brand focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring/40 aria-invalid:border-status-red-solid';

const sizeText = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('pt-BR')} KB`
    : `${(bytes / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;

export const collabKey = (subject: CollabSubjectName, subjectId: string) => ['collab', subject, subjectId];

/**
 * Comentários e anexos (T-112b, RF-41; DEC-047): comentários, arquivos e fotos (pré-visualização de imagem e
 * PDF), links e a marcação de evidência de execução de um objetivo, KPI, iniciativa ou ação.
 */
export function CollaborationPanel({
  organizationId,
  projectId,
  subject,
  subjectId,
  label,
}: {
  organizationId: string;
  projectId: string;
  subject: CollabSubjectName;
  subjectId: string;
  /** Nome do assunto (para os rótulos acessíveis). */
  label: string;
}) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const base = `/organizations/${organizationId}/projects/${projectId}/collaboration/${subject}/${subjectId}`;
  const key = collabKey(subject, subjectId);
  const list = useQuery({
    queryKey: key,
    queryFn: ({ signal }) => client.request(base, { schema: collabListSchema, signal }),
    // Os links dos arquivos são assinados e vencem: a lista é recarregada antes (na metade do prazo).
    refetchInterval: (query) => {
      const ttl = query.state.data?.urlTtlSeconds;
      return ttl ? Math.max(10_000, (ttl * 1000) / 2) : false;
    },
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const [removing, setRemoving] = useState<CollabItem | null>(null);
  // Só o id: a prévia lê o item atual da lista, com as URLs renovadas a cada recarga.
  const [previewId, setPreviewId] = useState<string | null>(null);
  const preview = list.data?.items.find((i) => i.id === previewId) ?? null;

  const evidence = useMutation({
    meta: { handlesError: true },
    mutationFn: (item: CollabItem) =>
      client.request(`${base}/items/${item.id}/evidence`, {
        method: 'PUT',
        body: { isEvidence: !item.isEvidence },
        schema: collabItemSchema,
      }),
    onSuccess: async (item) => {
      toast.success(item.isEvidence ? 'Marcado como evidência' : 'Marcação de evidência retirada');
      await refresh();
    },
    onError: (err) => toast.error('Não foi possível mudar a marcação', { description: errorMessage(err) }),
  });
  const remove = useMutation({
    meta: { handlesError: true },
    mutationFn: (item: CollabItem) => client.request(`${base}/items/${item.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setRemoving(null);
      toast.success('Item excluído');
      await refresh();
    },
    onError: (err) => toast.error('Não foi possível excluir', { description: errorMessage(err) }),
  });

  return (
    <section aria-label={`Comentários e anexos de ${label}`} className="flex flex-col gap-3">
      <h3 className="text-sm font-bold">Comentários e anexos</h3>
      {list.isPending ? (
        <LoadingState rows={2} label="Carregando comentários e anexos" />
      ) : list.isError ? (
        <ErrorState error={list.error} onRetry={() => void list.refetch()} retrying={list.isFetching} />
      ) : (
        <>
          {list.data.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum comentário ou anexo ainda.</p>
          ) : (
            <ol aria-label={`Itens de ${label}`} className="flex flex-col gap-2">
              {list.data.items.map((item) => (
                <ItemView
                  key={item.id}
                  item={item}
                  busy={evidence.isPending || remove.isPending}
                  onPreview={() => setPreviewId(item.id)}
                  onToggleEvidence={() => evidence.mutate(item)}
                  onRemove={() => setRemoving(item)}
                />
              ))}
            </ol>
          )}
          {list.data.canPost ? (
            <Composer base={base} onPosted={refresh} />
          ) : (
            <p className="text-xs text-muted-foreground">
              Arquivado: os comentários e anexos ficam só para consulta.
            </p>
          )}
        </>
      )}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Excluir item?"
        description={
          removing?.kind === 'FILE'
            ? `O arquivo "${removing.file?.name}" sai daqui e do armazenamento. A trilha de auditoria guarda o registro.`
            : 'O item sai daqui. A trilha de auditoria guarda o que era.'
        }
        confirmLabel="Excluir"
        destructive
        busy={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing)}
      />
      <Dialog
        open={preview !== null}
        onOpenChange={(open) => !open && setPreviewId(null)}
        title={preview?.file?.name ?? 'Pré-visualização'}
        className="w-[min(56rem,calc(100vw-2rem))]"
      >
        {preview?.file?.previewUrl && preview.file.isImage ? (
          <img
            src={preview.file.previewUrl}
            alt={preview.file.name}
            className="mx-auto max-h-[70dvh] w-auto rounded-md object-contain"
          />
        ) : null}
        {preview?.file ? (
          <div className="mt-3 flex justify-end">
            <Button asChild variant="outline" size="sm">
              <a href={preview.file.downloadUrl} target="_blank" rel="noopener noreferrer">
                <Download aria-hidden />
                Baixar
              </a>
            </Button>
          </div>
        ) : null}
      </Dialog>
    </section>
  );
}

function ItemView({
  item,
  busy,
  onPreview,
  onToggleEvidence,
  onRemove,
}: {
  item: CollabItem;
  busy: boolean;
  onPreview: () => void;
  onToggleEvidence: () => void;
  onRemove: () => void;
}) {
  const what =
    item.kind === 'COMMENT'
      ? 'comentário'
      : item.kind === 'LINK'
        ? 'link'
        : item.file?.isImage
          ? 'foto'
          : 'arquivo';
  const name =
    item.kind === 'FILE' ? item.file!.name : item.kind === 'LINK' ? (item.body ?? item.url!) : 'comentário';
  return (
    <li className="rounded-md border border-border px-3 py-2 text-sm">
      <article aria-label={`${what[0]!.toUpperCase()}${what.slice(1)} de ${item.author.name}`}>
        <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span className="font-semibold text-foreground">{item.author.name}</span>
          <span>{formatDateTime(item.createdAt)}</span>
          {item.isEvidence ? <StatusBadge status="green" label="Evidência de execução" /> : null}
        </p>
        {item.kind === 'COMMENT' ? (
          <p className="mt-1 whitespace-pre-line break-words">
            {splitMentions(item.body ?? '', item.mentions).map((part, i) =>
              part.mention ? (
                <span
                  key={i}
                  className="rounded-sm bg-brand-soft px-0.5 font-semibold text-brand-strong dark:text-brand"
                >
                  {part.text}
                </span>
              ) : (
                part.text
              ),
            )}
          </p>
        ) : null}
        {item.kind === 'LINK' ? (
          <p className="mt-1 flex items-center gap-1.5 break-all">
            <Link2 className="size-4 shrink-0 text-muted-foreground" aria-hidden />
            <a href={item.url!} target="_blank" rel="noopener noreferrer" className="font-medium underline">
              {item.body ?? item.url}
            </a>
          </p>
        ) : null}
        {item.kind === 'FILE' && item.file ? (
          <div className="mt-1 flex flex-wrap items-center gap-3">
            {item.file.isImage && item.file.previewUrl ? (
              <button
                type="button"
                onClick={onPreview}
                className="overflow-hidden rounded-md border border-border focus-visible:outline-2 focus-visible:outline-ring"
                aria-label={`Ver a foto ${item.file.name}`}
              >
                <img src={item.file.previewUrl} alt="" className="size-20 object-cover" />
              </button>
            ) : (
              <FileText className="size-6 text-muted-foreground" aria-hidden />
            )}
            <span className="min-w-0 break-all">
              {item.file.name}{' '}
              <span className="text-xs text-muted-foreground">({sizeText(item.file.size)})</span>
            </span>
            {!item.file.isImage && item.file.previewUrl ? (
              // PDF no visualizador do navegador (no app desktop e no Android, o do sistema): o quadro dentro
              // do app não funciona nessas plataformas (DEC-047).
              <Button asChild variant="ghost" size="sm">
                <a
                  href={item.file.previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`Ver o PDF ${item.file.name}`}
                >
                  Ver
                </a>
              </Button>
            ) : null}
            <Button asChild variant="ghost" size="sm">
              <a
                href={item.file.downloadUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Baixar ${item.file.name}`}
              >
                <Download aria-hidden />
                Baixar
              </a>
            </Button>
          </div>
        ) : null}
        {item.canDelete ? (
          <div className="mt-2 flex flex-wrap justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={onToggleEvidence}
              aria-label={
                item.isEvidence
                  ? `Retirar a marcação de evidência de ${name}`
                  : `Marcar ${name} como evidência`
              }
            >
              <ShieldCheck aria-hidden />
              {item.isEvidence ? 'Não é evidência' : 'Marcar como evidência'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={onRemove}
              aria-label={`Excluir ${name}`}
            >
              <Trash2 aria-hidden />
              Excluir
            </Button>
          </div>
        ) : null}
      </article>
    </li>
  );
}

function Composer({ base, onPosted }: { base: string; onPosted: () => Promise<unknown> }) {
  const { client } = useAuth();
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<MentionRef[]>([]);
  const [isEvidence, setIsEvidence] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState<'comment' | 'link' | 'file' | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const photoInput = useRef<HTMLInputElement>(null);
  const ids = { body: useId(), evidence: useId(), url: useId(), title: useId() };

  async function send(kind: 'comment' | 'link', payload: unknown) {
    setSaving(kind);
    try {
      await client.request(`${base}/${kind === 'comment' ? 'comments' : 'links'}`, {
        method: 'POST',
        body: payload,
        schema: collabItemSchema,
      });
      toast.success(kind === 'comment' ? 'Comentário enviado' : 'Link adicionado');
      setErrors({});
      setIsEvidence(false);
      if (kind === 'comment') {
        setBody('');
        setMentions([]);
      } else {
        setUrl('');
        setTitle('');
        setLinkOpen(false);
      }
      await onPosted();
    } catch (err) {
      const fields = apiFieldErrors(err);
      // Erro que não é de um campo aparece no formulário que foi enviado.
      const general = Object.keys(fields).length
        ? {}
        : { [kind === 'comment' ? 'form' : 'linkForm']: errorMessage(err) };
      setErrors({ ...fields, ...general });
    } finally {
      setSaving(null);
    }
  }

  async function comment(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    // Só quem continua mencionado no texto (a pessoa pode ter apagado o "@Nome" depois de escolher).
    const mentionIds = mentionsInText(body, mentions).map((m) => m.id);
    const parsed = collabCommentInputSchema.safeParse({ body, isEvidence, mentionIds });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    await send('comment', parsed.data);
  }

  async function link(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    const parsed = collabLinkInputSchema.safeParse({ url, title, isEvidence });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    await send('link', parsed.data);
  }

  async function attach(file: File | undefined) {
    if (!file || saving) return;
    // Tipo e tamanho conferidos antes de enviar (o servidor confere de novo, pelo conteúdo).
    const problem = evidenceFileProblem(file, 'O arquivo');
    if (problem) {
      toast.error('Arquivo não aceito', { description: problem });
      return;
    }
    setSaving('file');
    try {
      const form = new FormData();
      form.append('file', file);
      await client.request(`${base}/files${isEvidence ? '?evidencia=true' : ''}`, {
        method: 'POST',
        body: form,
        schema: collabItemSchema,
      });
      toast.success(file.type.startsWith('image/') ? 'Foto anexada' : 'Arquivo anexado', {
        description: file.name,
      });
      setIsEvidence(false);
      await onPosted();
    } catch (err) {
      toast.error('Não foi possível anexar', { description: errorMessage(err) });
    } finally {
      setSaving(null);
    }
  }

  return (
    <div className="flex flex-col gap-3 rounded-md border border-dashed border-border p-3">
      <form noValidate onSubmit={comment} className="flex flex-col gap-2">
        <FormField
          id={ids.body}
          label="Comentário"
          error={errors.body ?? errors.mentionIds}
          hint={
            <p className="text-xs text-muted-foreground">Digite @ para mencionar quem acompanha este item.</p>
          }
        >
          <MentionTextarea
            {...fieldA11y(ids.body, errors.body ?? errors.mentionIds, true)}
            value={body}
            onValueChange={setBody}
            mentions={mentions}
            onMentionsChange={setMentions}
            mentionableUrl={`${base}/mentionable`}
            rows={3}
            maxLength={COLLAB_COMMENT_MAX}
            placeholder="Escreva um comentário, uma atualização ou o que foi feito"
            className={textareaClass}
          />
        </FormField>
        <label htmlFor={ids.evidence} className="flex items-start gap-2 text-sm">
          <input
            id={ids.evidence}
            type="checkbox"
            className="mt-0.5 size-4 shrink-0 accent-(--brand)"
            checked={isEvidence}
            onChange={(e) => setIsEvidence(e.target.checked)}
          />
          <span>
            Marcar como evidência de execução
            <span className="block text-xs text-muted-foreground">
              Vale para o próximo comentário, link ou anexo.
            </span>
          </span>
        </label>
        {errors.form ? (
          <p role="alert" className="text-sm text-status-red-fg">
            {errors.form}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-2">
          <Button
            type="submit"
            size="sm"
            loading={saving === 'comment'}
            disabled={saving !== null && saving !== 'comment'}
          >
            Comentar
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            loading={saving === 'file'}
            disabled={saving !== null && saving !== 'file'}
            onClick={() => fileInput.current?.click()}
          >
            <Paperclip aria-hidden />
            Anexar arquivo
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={saving !== null}
            onClick={() => photoInput.current?.click()}
          >
            <Camera aria-hidden />
            Tirar foto
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={saving !== null}
            aria-expanded={linkOpen}
            onClick={() => setLinkOpen((v) => !v)}
          >
            <Link2 aria-hidden />
            Adicionar link
          </Button>
        </div>
        {/* Arquivo do computador ou do celular; "Tirar foto" abre a câmera no celular (input de captura). */}
        <input
          ref={fileInput}
          type="file"
          accept={EVIDENCE_ACCEPT}
          className="sr-only"
          tabIndex={-1}
          aria-label="Arquivo para anexar"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            void attach(file);
          }}
        />
        <input
          ref={photoInput}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          tabIndex={-1}
          aria-label="Foto para anexar"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            void attach(file);
          }}
        />
      </form>
      {linkOpen ? (
        <form noValidate onSubmit={link} className="grid gap-2 sm:grid-cols-2" aria-label="Novo link">
          <FormField id={ids.url} label="Endereço do link" error={errors.url}>
            <Input
              {...fieldA11y(ids.url, errors.url)}
              value={url}
              inputMode="url"
              placeholder="https://"
              onChange={(e) => setUrl(e.target.value)}
              className="h-10"
            />
          </FormField>
          <FormField id={ids.title} label="Título (opcional)" error={errors.title}>
            <Input
              {...fieldA11y(ids.title, errors.title)}
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              className="h-10"
            />
          </FormField>
          {errors.linkForm ? (
            <p role="alert" className="text-sm text-status-red-fg sm:col-span-2">
              {errors.linkForm}
            </p>
          ) : null}
          <div className="sm:col-span-2">
            <Button type="submit" size="sm" variant="outline" loading={saving === 'link'}>
              Salvar link
            </Button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
